import React, { useEffect, useState } from 'react';
import LineChartPeaksPreview from '../shared/LineChartPeaksPreview';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { selectAreaUnderPeaksQc, selectAreaUnderPeaksUnk } from '../../lib/utilities/selector';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import { selectQcFilesData, selectUnkFilesData } from '../../lib/utilities/selector';
import { selectPeaksObject } from '../../lib/utilities/selector';
import { MdOutlineChevronRight } from "react-icons/md";
import { savePeaksObject } from '../../reduxSlices/results/resultsSlice';
// import saveResultsToFirebase from '../../lib/utilities/saveResultsToFirebase';
import sanitizeKeysDeep from '../../lib/utilities/sanitizeKeyDeep';
import { v4 as uuidv4 } from 'uuid';
import MessageBox from '../shared/MessageBox';
import ResizableDiv from '../shared/ResizableDiv';
import BackButton from '../shared/BackButton';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useConfigNavigationGuard } from '../../hooks/useConfigNavigationGuard';
import { resetAll } from '../../reduxSlices/results/resultsSlice';
// Generate or retrieve from localStorage
const getOrCreateConfigId = () => {
    const existing = localStorage.getItem('currentConfigId');
    if (existing) return existing;

    const newId = uuidv4();
    localStorage.setItem('currentConfigId', newId);
    return newId;
};

const configId = getOrCreateConfigId();




const PeaksOnQc = () => {

    const areaUnderPeaksQcArray = useSelector(selectAreaUnderPeaksQc) || [];
    const areaUnderPeaksUnkArray = useSelector(selectAreaUnderPeaksUnk) || [];
    const qcFilesDataRaw = useSelector(selectQcFilesData);
    const unkFilesDataRaw = useSelector(selectUnkFilesData);
    const peaksObject = useSelector(selectPeaksObject) || [];

    // Normalize to arrays to avoid runtime errors
    const qcFilesData = Array.isArray(qcFilesDataRaw) ? qcFilesDataRaw : [];
    const unkFilesData = Array.isArray(unkFilesDataRaw) ? unkFilesDataRaw : [];


    const [initialPeaks, setInitialPeaks] = useState(null);
    const [editableValues, setEditableValues] = useState({});
    const [errors, setErrors] = useState({});

    // const [selectedPeakIndex, setSelectedPeakIndex] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate()

    // Navigation guard
    const { showExitModal, handleConfirmExit, handleCancelExit } = useConfigNavigationGuard(
        ['/configuration/showAllFiles', '/configuration/calibrationCurve'],
        () => dispatch(resetAll())
    );

    // Pick the last QC series key if available; otherwise undefined
    const initialVisibleChartKey = (Array.isArray(qcFilesData) && qcFilesData.length > 0)
        ? Object.keys(qcFilesData[qcFilesData.length - 1] || {})[0]
        : undefined;


    useEffect(() => {
        if (!peaksObject || !qcFilesData || !areaUnderPeaksQcArray || !unkFilesData || !areaUnderPeaksUnkArray) return;

        // Create the prefilled structure for QC values
        const qcTcValues = qcFilesData.map(obj => {
            const key = Object.keys(obj || {})[0]; // Extract key from each object
            return { [key]: '' };
        });

        // Create the prefilled structure for QC RSD values
        const qcRsdValues = qcFilesData.map(obj => {
            const key = Object.keys(obj || {})[0];
            return { [key]: '' };
        });

        // Create the prefilled structure for Unknown (UNK) values
        const unkTcValues = unkFilesData.map(obj => {
            const key = Object.keys(obj || {})[0]; // Extract key from each object
            return { [key]: '' };
        });

        const updatedPeaks = peaksObject.map((peak, peakIndex) => {
            if (peak.show) {
                const qcErrors = {};
                const qcTc = qcTcValues.map(tcObj => {
                    const tcKey = Object.keys(tcObj || {})[0];
                    const areaUnderPeakQc = areaUnderPeaksQcArray[peakIndex]?.[tcKey];
                    const coefficients = peak.equation?.coefficients;
                    let calculatedValue = '';

                    if (coefficients && areaUnderPeakQc !== undefined) {
                        calculatedValue = parseFloat(
                            (coefficients[0] * areaUnderPeakQc + coefficients[1]).toFixed(4)
                        );
                    }

                    qcErrors[tcKey] = 0;

                    return { [tcKey]: calculatedValue };
                });

                // Prefill `unkConcentrations` using `unkFilesData`
                const unkConcentrations = unkTcValues.map(tcObj => {
                    const tcKey = Object.keys(tcObj || {})[0];
                    const areaUnderPeakUnk = areaUnderPeaksUnkArray[peakIndex]?.[tcKey];
                    const coefficients = peak.equation?.coefficients;
                    let calculatedValue = '';

                    if (coefficients && areaUnderPeakUnk !== undefined) {
                        calculatedValue = parseFloat(
                            (coefficients[0] * areaUnderPeakUnk + coefficients[1]).toFixed(4)
                        );
                    }

                    return { [tcKey]: calculatedValue };
                });

                return {
                    ...peak,
                    qcTc,
                    qcTargetRSD: peak.qcTargetRSD || qcRsdValues.map(rsdObj => ({ ...rsdObj })), // Initialize or preserve existing RSD values
                    qcErrors, // Initialized with 0
                    qcConcentrations: [...qcTc],  // Prefilled values for QC
                    unkConcentrations: [...unkConcentrations], // Prefilled values for Unknown (UNK) samples
                };
            }
            return peak;
        });
        setInitialPeaks(updatedPeaks);
    }, [peaksObject, qcFilesData, areaUnderPeaksQcArray, unkFilesData, areaUnderPeaksUnkArray]);


    const handleQcTCChange = (peakIndex, tcKey, newTC) => {
        if (initialPeaks !== null) {
            const measuredConcentration = qcTcFinder(peakIndex, tcKey); // Pre-filled value
            const error = calculateConcentrationError(newTC, measuredConcentration);

            const updatedPeaks = initialPeaks.map((peak, idx) => {
                if (idx === peakIndex) {
                    return {
                        ...peak,
                        qcTc: peak.qcTc.map(tcObj => {
                            const key = Object.keys(tcObj || {})[0];
                            if (key === tcKey) {
                                // Update the user-modified field and mark it as edited
                                return { [tcKey]: newTC === "" ? "" : parseFloat(newTC) || "", userEdited: true };
                            }
                            return tcObj; // Keep other fields intact
                        }),
                        qcErrors: {
                            ...peak.qcErrors, // Preserve existing errors
                            [tcKey]: error,  // Add or update the error for the current `tcKey`
                        },
                    };
                }
                return peak;
            });

            setInitialPeaks(updatedPeaks); // Update state

            setEditableValues(prevState => ({
                ...prevState,
                [`${peakIndex}-${tcKey}`]: newTC, // Store the user-modified value
            }));

            setErrors(prevErrors => ({
                ...prevErrors,
                [`${peakIndex}-${tcKey}`]: error, // Store the calculated error
            }));
        }
    };

    const handleQcRSDChange = (peakIndex, rsdKey, newRSD) => {
        if (initialPeaks !== null) {
            const updatedPeaks = initialPeaks.map((peak, idx) => {
                if (idx === peakIndex) {
                    return {
                        ...peak,
                        qcTargetRSD: peak.qcTargetRSD.map(rsdObj => {
                            const key = Object.keys(rsdObj)[0];
                            if (key === rsdKey) {
                                return { [rsdKey]: newRSD === "" ? "" : parseFloat(newRSD) || "" };
                            }
                            return rsdObj;
                        }),
                    };
                }
                return peak;
            });
            setInitialPeaks(updatedPeaks);

            // Force a re-render to update the color comparison
            setErrors(prevErrors => ({ ...prevErrors }));
        }
    };

    const handleNextScreen = () => {
        // Always save to Redux since this component adds/calculates important QC and UNK data
        // Even if user doesn't modify anything, the calculated qcTc, qcErrors, qcConcentrations, 
        // and unkConcentrations need to be saved for subsequent components
        dispatch(savePeaksObject(initialPeaks));

        // Also save to Firebase
        (async () => {
            try {
                // await saveResultsToFirebase(`${configId}/peaksObject`, sanitizeKeysDeep(initialPeaks));
            } catch (error) {
                console.error('Error saving peaksObject to Firebase:', error);
            }
            navigate('/configuration/showAllFiles');
        })();
    }




    const qcTcFinder = (peakIndex, tcKey) => {
        if (!initialPeaks || !areaUnderPeaksQcArray) return '';
        const coefficients = initialPeaks[peakIndex]?.equation?.coefficients;
        const areaUnderPeakQc = areaUnderPeaksQcArray[peakIndex]?.[tcKey];
        if (!coefficients || areaUnderPeakQc === undefined) return '';

        const resultTc = coefficients[0] * areaUnderPeakQc + coefficients[1];
        return parseFloat(resultTc.toFixed(4));
    };


    const calculateConcentrationError = (targetConcentration, measuredConcentration) => {
        /**
         * Calculate the percentage error for a single measured concentration
         * relative to its target concentration.
         *
         * @param {number} targetConcentration - The target concentration value.
         * @param {number} measuredConcentration - The measured concentration value.
         * @return {number|null} - The percentage error rounded to two decimals, or null if the measured value is undefined.
         */
        if (measuredConcentration === undefined) {
            // Handle missing value
            return null;
        } else {
            // Calculate percentage error
            const errorPercentage = (Math.abs(measuredConcentration - targetConcentration) / targetConcentration) * 100;
            return Math.round(errorPercentage * 100) / 100; // Round to two decimals
        }
    };




    return (
        <div className='flex flex-col overflow-hidden' >
            <ConfirmationModal
                isOpen={showExitModal}
                onClose={handleCancelExit}
                onConfirm={handleConfirmExit}
                title="Leave Configuration?"
                message="If you leave now, the current configuration will not be saved and all progress will be lost. Are you sure you want to continue?"
                confirmText="Leave"
                cancelText="Stay"
                type="warning"
            />
            <ResizableDiv initialHeight={500} minHeight={400} className='rounded-sm shadow-md px-5 py-2 m-8 '>
                <LineChartPeaksPreview data={qcFilesData || []} initialVisibleChart={initialVisibleChartKey} reportingPeaks={initialPeaks} />
            </ResizableDiv>

            <div className='flex-grow flex justify-center items-center overflow-auto mb-24 mt-10'>
                <table className=''>
                    {/* <caption className="w-full text-end mb-2 relative">
            <span className="text-cyan-500 relative group hover:cursor-help inline-block">
                What is RSD?
                <div className="absolute bottom-full right-0 min-w-max p-2 text-white text-sm invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 pointer-events-none whitespace-nowrap z-40">
                    <MessageBox message={'Relative Standard Deviation (RSD) is a measure of the variability of the data. It is calculated as the standard deviation divided by the mean, and then multiplied by 100 to express it as a percentage.'} />
                
                </div>
            </span>
        </caption> */}
                    <caption className='caption-bottom text-sm italic text-gray-600 text-left mt-1'><span className='text-cyan-500 font-bold'>Relative Standard Deviation (RSD)</span> is a measure of the variability of the data. It is calculated as the standard deviation divided by the mean, and then multiplied by 100 to express it as a percentage.</caption>



                    <tbody>

                        <tr className=''>
                            <td className='text-center bg-customeBlack text-gray-200 rounded-tl-sm'>Peak Name</td>
                            {initialPeaks && initialPeaks.map((value, index) => (
                                value.show && (
                                    <td key={`name-${index}`} className={'border-x-2 border-gray-300 text-center bg-cyan-500 font-medium text-cyan-100'}>
                                        {value.name}
                                    </td>
                                )
                            ))}
                        </tr>

                        {(qcFilesData || []).map((obj, tcIndex) => {
                            const tcKey = Object.keys(obj || {})[0] || ""; // Get the key for this row
                            return (
                                <tr key={`tc-row-${tcIndex}`} className='border-2 border-gray-300'>
                                    <td className=' text-center text-white bg-customeBlack'>
                                        <span className='font-semibold text-customeOrange'>{tcKey.split('.').slice(0, -1).join('.')}</span>
                                    </td>
                                    {initialPeaks && initialPeaks.map((value, peakIndex) => (
                                        value.show && (
                                            <td key={`tc-${tcIndex}-${peakIndex}`} className={'border-x-2 border-gray-300 items-center bg-gray-200 p-2'}>
                                                <div className='flex flex-col gap-3'>
                                                    <div className='flex flex-col'>
                                                        <div className='flex items-center gap-1'>
                                                            <input
                                                                className='p-1 w-20 rounded-sm shadow-sm text-gray-500'
                                                                value={
                                                                    editableValues[`${peakIndex}-${tcKey}`] !== undefined
                                                                        ? editableValues[`${peakIndex}-${tcKey}`] // Show user-modified input as-is
                                                                        : value.qcTc.find(tcObj => Object.keys(tcObj)[0] === tcKey)?.[tcKey] !== undefined
                                                                            ? parseFloat(value.qcTc.find(tcObj => Object.keys(tcObj)[0] === tcKey)[tcKey]).toFixed(1) // Show prefilled values with 1 decimal
                                                                            : ''
                                                                }
                                                                type="text" // Prevents forced auto-formatting of numbers
                                                                placeholder="Target Concentration"
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;

                                                                    // Allow only valid number input including decimals
                                                                    if (/^\d*\.?\d*$/.test(inputValue)) {
                                                                        setEditableValues(prevState => ({
                                                                            ...prevState,
                                                                            [`${peakIndex}-${tcKey}`]: inputValue, // Store input as raw text
                                                                        }));

                                                                        // Call handleQcTCChange to update RSD dynamically
                                                                        handleQcTCChange(peakIndex, tcKey, inputValue);
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setEditableValues(prevState => {
                                                                        const currentValue = prevState[`${peakIndex}-${tcKey}`];

                                                                        // If the input was never modified, do not change it
                                                                        if (currentValue === undefined || currentValue === '') {
                                                                            return prevState;
                                                                        }

                                                                        return {
                                                                            ...prevState,
                                                                            [`${peakIndex}-${tcKey}`]: parseFloat(currentValue).toFixed(1) // Convert toFixed(1) only if modified
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                            <span className='text-xs text-gray-600 font-medium'>ppm</span>
                                                            <div className='ml-2 items-center justify-center'>
                                                                <div className='font-bold text-center text-cyan-500'>RSD</div>
                                                                <div className={`text-center w-16 font-semibold ${errors[`${peakIndex}-${tcKey}`] !== undefined &&
                                                                        value.qcTargetRSD?.find(rsdObj => tcKey in rsdObj)?.[tcKey] &&
                                                                        errors[`${peakIndex}-${tcKey}`] > parseFloat(value.qcTargetRSD?.find(rsdObj => tcKey in rsdObj)?.[tcKey] || 0)
                                                                        ? 'text-red-500'
                                                                        : 'text-gray-900'
                                                                    }`}>
                                                                    {errors[`${peakIndex}-${tcKey}`] !== undefined ? `${errors[`${peakIndex}-${tcKey}`]}%` : '0%'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='flex flex-col'>
                                                        <div className='flex items-center gap-1'>
                                                            <input
                                                                className='p-1 w-20 rounded-sm shadow-sm text-gray-500'
                                                                value={value.qcTargetRSD?.find(rsdObj => tcKey in rsdObj)?.[tcKey] || ''}
                                                                type="number"
                                                                placeholder="RSD %"
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    const parsedValue = inputValue === '' ? '' : parseFloat(inputValue);
                                                                    handleQcRSDChange(peakIndex, tcKey, parsedValue);
                                                                }}
                                                            />
                                                            <span className='text-xs text-gray-600 font-medium'>%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        )
                                    ))}
                                </tr>
                            );
                        })}

                    </tbody>
                </table>
            </div>
            <div className='fixed bottom-8 right-8 z-40 flex gap-2'>
                <BackButton to={'/configuration/calibrationCurve'} />

                <button
                    // disabled={!allTcValuesFilled}
                    onClick={() => handleNextScreen()}
                    className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
                >Next
                    <MdOutlineChevronRight className='ml-6' fontSize={24} />
                </button>
            </div>




        </div>

    )
}


export default PeaksOnQc